import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import type { ColDef, ITooltipParams, ValueFormatterParams } from 'ag-grid-community';

import { SYMBOL_FOR_EMPTY_CELL } from 'components/AgGrid/helpers';
import { formatAmount } from 'components/AgGrid/helpers/numberFormat';
import { DEFAULT_COLUMN_FLEX } from 'components/AgGrid/BankFeedTransactions/columnWidths';
import { currentCompanyDateFormatSelector } from 'domain/companies';
import type { BankFeedTransactionAccount, BankFeedTransactionRow } from 'domain/bankFeed/schemas';

// Custom-renderer cells need explicit vertical centering (their outer wrapper is content-sized
// and sits at the top of the row by default). Plain-text types (`string` / `select`) are left
// alone — AG Grid + themeMaterial center text against `rowHeight` natively, and avoiding
// `display: flex` there preserves built-in `text-overflow: ellipsis`. `account` self-centers via
// the renderer's own Stack `height="100%"`.
const CENTERED_CELL_STYLE: ColDef['cellStyle'] = { display: 'flex', alignItems: 'center' };

// Generic sizing default applied to every column type, `date` included — flexible columns share
// the grid's free space and `minWidth` is the shrink floor. Field-level overrides for the feedback-
// targeted columns (account / description / debtor_name / debit / credit) are layered on top in the
// useColumnDefs pipeline — see columnWidths.ts. Sizing is keyed by `type` (not by BE `field`) so a
// backend that delivers a different column set (e.g. the credit-card grid) is sized correctly
// without any frontend change.
const FLEX_DEFAULT = { flex: DEFAULT_COLUMN_FLEX, minWidth: 120 } satisfies Pick<ColDef, 'flex' | 'minWidth'>;

const useAgGridColumnTypes = () => {
  const companyDateFormat = useSelector(currentCompanyDateFormatSelector);
  const dateFormat = companyDateFormat || 'DD/MM/YYYY';

  return useMemo<Record<string, ColDef<BankFeedTransactionRow>>>(
    () => ({
      account: {
        ...FLEX_DEFAULT,
        cellRenderer: 'accountCellRenderer',
        tooltipValueGetter: (params: ITooltipParams<BankFeedTransactionRow, BankFeedTransactionAccount>) =>
          params.value?.name ?? '',
      },
      transaction_status: { ...FLEX_DEFAULT, cellRenderer: 'statusCell', cellStyle: CENTERED_CELL_STYLE },
      amount: {
        ...FLEX_DEFAULT,
        cellRenderer: 'amountCell',
        cellStyle: CENTERED_CELL_STYLE,
        // BE sends credit/debit as plain float strings ("2080", "-945") or null.
        // Format to en-US "2,080.00" for display (Workspace-grid parity); the sum
        // panel reads the raw value via getCellValue, so this stays display-only.
        valueFormatter: ({ value }: ValueFormatterParams<BankFeedTransactionRow, string | null>) =>
          value ? formatAmount(value) : SYMBOL_FOR_EMPTY_CELL,
      },
      string: { ...FLEX_DEFAULT },
      date: {
        // Flex like the other columns: a formatted date is constant-size so it never needs to be
        // wide, but it shares the grid's free space and can be resized freely (DA-19397 — the
        // former `maxWidth: 160` cap blocked widening past 160px). On a narrow (13") screen it
        // sits at `minWidth` and the header truncates — accepted.
        ...FLEX_DEFAULT,
        // `filterParams.format` feeds the shared `customDateFilter` picker the company date
        // format (same source as the cell `valueFormatter` below). Applied via columnTypes —
        // not `addFilters` — because it needs the Redux-derived format (parity with the
        // Documents/Workspace grid's `useAgGridColumnTypes`). AG Grid merges it with the
        // `filter`/`buttons` filterParams that the useColumnDefs pipeline sets.
        filterParams: {
          format: dateFormat,
        },
        valueFormatter: ({ value }: ValueFormatterParams) =>
          value ? moment(value).format(dateFormat) : SYMBOL_FOR_EMPTY_CELL,
      },
      // TODO: swap to a custom list/set filter component when bank-feed list-filter UX lands.
      select: { ...FLEX_DEFAULT },
    }),
    [dateFormat],
  );
};

export default useAgGridColumnTypes;
